import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, ZEFFY_URL, INSTAGRAM_URL } from "@/lib/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-10 z-50 transition-all duration-300 ${
        scrolled ? "bg-ivory/90 shadow-[0_1px_0_0_#E2DCD0] backdrop-blur-md" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" aria-label="Nseya Foundation home">
          <img
            src="/brand/nseya-logo.png"
            alt="Nseya Foundation logo"
            className="h-11 w-11 rounded-full bg-white object-contain p-1 shadow-sm"
          />
          <span className="leading-tight">
            <span className={`block font-display text-xl font-semibold tracking-wide ${scrolled ? "text-forest" : "text-ivory"}`}>
              NSEYA
            </span>
            <span className={`block font-mono text-[9px] uppercase tracking-[0.24em] ${scrolled ? "text-mutedink" : "text-ivory/70"}`}>
              Foundation · Change for Congo
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-gold ${
                pathname === l.to ? (scrolled ? "text-forest underline decoration-gold underline-offset-8" : "text-ivory underline decoration-gold underline-offset-8") : scrolled ? "text-ink/80" : "text-ivory/85"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={ZEFFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-donate-button"
            className="group inline-flex items-center gap-1.5 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold uppercase tracking-widest text-forest transition-all hover:bg-golddark hover:shadow-lg"
          >
            Donate
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <button
          className={`rounded-full p-2 lg:hidden ${scrolled ? "text-forest" : "text-ivory"}`}
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="nav-mobile-menu-button"
        >
          <Menu className="h-7 w-7" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-forest px-6 pb-6 pt-24 text-ivory"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between">
              <img src="/brand/nseya-logo.png" alt="Nseya Foundation logo" className="h-11 w-11 rounded-full bg-white object-contain p-1" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-mobile-close-button" className="rounded-full p-2">
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="mt-12 flex flex-col gap-6" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 * i }}>
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-display text-4xl font-medium"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto space-y-4">
              <a
                href={ZEFFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-donate-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-semibold uppercase tracking-widest text-forest"
              >
                Donate <ArrowUpRight className="h-5 w-5" />
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="block text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/70" data-testid="nav-mobile-instagram-link">
                @changeforcongo
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
